import React, { useEffect, useState } from "react";
import Loader from "../common/Loader";
import { useLocalization } from "../localization/LocalizationContext";
import allApi from "../../../api/allApi";
import Modal from "react-bootstrap/Modal";
import errorIcon from "../../../assets/images/error-2.png";
import Button from "react-bootstrap/Button";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import { useNetwork } from "../common/networkContext";
import NmsTableSearch, { searchRows } from "../common/NmsTableSearch";

function Anomalies() {
  const { localizationStrings } = useLocalization();
  const { selectedNetworkId, selectedLocation } = useNetwork();
  const networkId = selectedNetworkId?.id ?? 6001;
  const [anomaliesListing, setAnomaliesListing] = useState();
  const [loading, setLoading] = useState(false);
  const [filteredAnomalies, setFilteredAnomalies] = useState([]);
  const [searchFilters, setSearchFilters] = useState({});
  const [sortOrder, setSortOrder] = useState({});
  const [refreshing, setRefreshing] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [anomaliesPerPage] = useState(10);
  const [anomalyId, setAnomalyId] = useState();
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showFailurePopup, setShowFailurePopup] = useState(false);
  const [trendsData, setTrendsData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [anomalyTypeData, setAnomalyTypeData] = useState([]);
  const [tableQuery, setTableQuery] = useState("");

  useEffect(() => {
    getAnomaliesDetails(networkId);
    getAnomaliesCount(networkId);
    getAnomalyTypeData(networkId);
  }, [networkId]);

  const getAnomaliesDetails = async () => {
    setRefreshing(true);
    try {
      setLoading(true);
      const api = new allApi();
      const anomaliesResponse = await api.getRequest(
        `api/anomalies/getAllAnomalies?networkId=${networkId}`
      );
      if (anomaliesResponse) {
        setAnomaliesListing(anomaliesResponse);
        setFilteredAnomalies(
          searchRows(anomaliesResponse, tableQuery, [
            "serialNo",
            "deviceType",
            "model",
            "anomalyType",
            "summary",
            "anomalyCount",
          ])
        );
        setAnomalyId(anomaliesResponse.anomalyId);
      }
      console.log("amomaly id", anomaliesResponse?.anomalyId);
      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error("Error fetching anomalies:", error);
      setLoading(false);
    }
  };

  const getAnomaliesCount = async () => {
    setRefreshing(true);
    try {
      setLoading(true);
      const api = new allApi();
      const anomaliesResponse = await api.getRequest(
        `api/anomalies/anomalyCount?networkId=${networkId}`
      );

      if (anomaliesResponse && anomaliesResponse.data) {
        // Map API response to chart-friendly format
        const formattedData = anomaliesResponse.data.map(item => ({
          alert_time: item.interval1,      // X-axis
          anomaly_count: item.count,       // Y-axis
        }));

        setTrendsData(formattedData); // trendsData is used in your LineChart
      } else {
        setTrendsData([]);
      }

      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error("Error fetching anomalies:", error);
      setLoading(false);
      setRefreshing(false);
    }
  };
  const getAnomalyTypeData = async () => {

    setRefreshing(true);
    try {
      setLoading(true);

      const api = new allApi();
      const response = await api.getRequest(`api/anomalies/anomalyType?networkId=${networkId}`);

      console.log(" Anomaly Type API response:", response);

      const dailyData = response?.data?.dailyData;

      if (Array.isArray(dailyData) && dailyData.length > 0) {
        const formattedData = dailyData.map((item) => {
          return {
            alert_time: item.date || "", 
            sudden_spike_drop:
              typeof item.sudden_spike_drop === "number"
                ? item.sudden_spike_drop
                : 0,
            high_multicast_ratio:
              typeof item.high_multicast_ratio === "number"
                ? item.high_multicast_ratio
                : 0,
            unbalanced_radio_traffic:
              typeof item.unbalanced_radio_traffic === "number"
                ? item.unbalanced_radio_traffic
                : 0,
            per_client_anomaly:
              typeof item.per_client_anomaly === "number"
                ? item.per_client_anomaly
                : 0,
          };
        });

        console.log("Formatted Data for Recharts:", formattedData);
        setAnomalyTypeData(formattedData);
      } else {
        console.warn("No dailyData found in response!");
        setAnomalyTypeData([]);
      }
    } catch (error) {
      console.error("Error fetching anomaly type data:", error);
      setAnomalyTypeData([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSolution = async (id) => {
    try {
      const api = new allApi();
      const payload = {
        anomalyId: id,
      };
      const solutionResponse = await api.postRequest(
        "api/anomalies/resolveAnomaly",
        "",
        payload
      );
      if (solutionResponse.status === "200") {
        setShowSuccessPopup(true);
        getAnomaliesDetails(networkId);
      } else if (solutionResponse.status === "400") {
        setErrorMessage(solutionResponse.message || "Unknown error");
        setShowFailurePopup(true);
      }
    } catch (error) {
      setErrorMessage("Device does not support this configuration.");
      setShowFailurePopup(true);
    }
  };

  const handleSearch = (e) => {
    handleTableSearch(e.target.value);
  };

  const handleTableSearch = (value) => {
    setTableQuery(value);
    const source = Array.isArray(anomaliesListing) ? anomaliesListing : [];
    setFilteredAnomalies(
      searchRows(source, value, [
        "serialNo",
        "deviceType",
        "model",
        "anomalyType",
        "summary",
        "anomalyCount",
      ])
    );
  };

  const handleSortChange = (column) => {
    const order = sortOrder[column] === "asc" ? "desc" : "asc";
    const sorted = [...filteredAnomalies].sort((a, b) => {
      if (!a[column]) return 1;
      if (!b[column]) return -1;
      if (a[column] < b[column]) return order === "asc" ? -1 : 1;
      if (a[column] > b[column]) return order === "asc" ? 1 : -1;
      return 0;
    });

    setSortOrder({ [column]: order });
    setFilteredAnomalies(sorted);
  };

  const totalFilteredAnomalies = filteredAnomalies.length;
  const totalPages = Math.ceil(totalFilteredAnomalies / anomaliesPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleFirstPage = () => {
    setCurrentPage(1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleLastPage = () => {
    setCurrentPage(totalPages);
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    if (
      (currentPage - 1) * anomaliesPerPage >= totalFilteredAnomalies &&
      currentPage !== 1
    ) {
      setCurrentPage(1);
    }
  }, [currentPage, anomaliesPerPage, totalFilteredAnomalies]);

  const indexOfLastAnomaly = currentPage * anomaliesPerPage;
  const indexOfFirstAnomaly = indexOfLastAnomaly - anomaliesPerPage;

  const currentAnomalies = filteredAnomalies.slice(
    indexOfFirstAnomaly,
    indexOfLastAnomaly
  );

  const handleCancelPopUp = () => {
    setShowSuccessPopup(false);
  };

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="row mb-4">
          <div className="col-lg-6">
            <div className="inc-card">
              <div className="inc-card-title">
                <h3>{localizationStrings?.anomaliesTrends}</h3>
              </div>
              <div className="inc-card-body">
                {trendsData.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "50px" }}>
                    No Data Available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                      data={trendsData}
                      margin={{ top: 20, right: 30, left: 10, bottom: 30 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />

                      {/* X Axis with Label */}
                      <XAxis
                        dataKey="alert_time"
                        tickFormatter={(value) =>
                          new Date(value).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })
                        }
                        label={{
                          value: "Date",
                          position: "insideBottom",
                          offset: -20,
                        }}
                      />

                      {/* Y Axis with Label */}
                      <YAxis
                        label={{
                          value: "Anomaly Count",
                          angle: -90,
                          position: "insideLeft",
                        }}
                      />

                      {/* Tooltip */}
                      <Tooltip
                        labelFormatter={(label) =>
                          new Date(label).toLocaleDateString("en-US", {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                          })
                        }
                        formatter={(value) => [value, "Count"]}
                      />

                      <Line
                        type="monotone"
                        dataKey="anomaly_count"
                        stroke="#8884d8"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="inc-card">
              <div className="inc-card-title">
                <h3>{localizationStrings?.anomaliesTypes}</h3>
              </div>
              <div className="inc-card-body">
                {anomalyTypeData.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "50px" }}>
                    No Data Available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart
                      data={anomalyTypeData}
                      margin={{ top: 20, right: 30, left: 10, bottom: 10 }} // give space for legend
                    >
                      <CartesianGrid strokeDasharray="3 3" />

                      {/* X Axis */}
                      <XAxis
                        dataKey="alert_time"
                        tickFormatter={(value) =>
                          value // since your API gives short date like 'Sep 19'
                        }
                        label={{
                          position: "insideBottom",
                          offset: -10,
                        }}
                      />

                      {/* Y Axis */}
                      <YAxis
                        label={{
                          value: "Anomaly Count",
                          angle: -90,
                          position: "insideLeft",
                        }}
                      />

                      {/* Tooltip */}
                      <Tooltip
                        labelFormatter={(label) => `Date: ${label}`}
                        formatter={(value) => [value, "Count"]}
                      />

                      {/* ✅ Legend below the graph */}
                      <Legend
                        verticalAlign="bottom"
                        align="center"
                        layout="horizontal"
                        wrapperStyle={{
                          marginTop: "30px",
                          paddingTop: "10px",
                        }}
                      />

                      {/* Each anomaly line */}
                      <Line
                        type="monotone"
                        dataKey="sudden_spike_drop"
                        name="Sudden Spike/Drop"
                        stroke="#8884d8"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="high_multicast_ratio"
                        name="High Multicast Ratio"
                        stroke="#82ca9d"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="unbalanced_radio_traffic"
                        name="Unbalanced Radio Traffic"
                        stroke="#ffc658"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="per_client_anomaly"
                        name="Per Client Anomaly"
                        stroke="#ff7300"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="inc-card ch-100">
        <div className="inc-card-title">
          <h3>{localizationStrings?.anomalies}</h3>
          <div className="inc-card-button">
            <NmsTableSearch
              value={tableQuery}
              onChange={handleTableSearch}
              placeholder={localizationStrings?.search || "Search anomalies"}
            />
            <button
              className="icon-btn"
              title="Refresh"
              onClick={() => getAnomaliesDetails(networkId)}
            >
              {refreshing ? (
                <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
              ) : (
                <i className="fa fa-refresh" aria-hidden="true"></i>
              )}
            </button>
          </div>
        </div>
        <div className="inc-card-body">
          {loading ? (
            <Loader />
          ) : (
            <div className="inc-card-table">
              <table className="full-table">
                <thead>
                  <tr>
                    <th
                      className="inc-th-sort"
                      onClick={() => handleSortChange("serialNo")}
                    >
                      {localizationStrings?.serialNumber || "Serial No"}
                      <i className="fa fa-sort" aria-hidden="true" />
                    </th>
                    <th
                      className="inc-th-sort"
                      onClick={() => handleSortChange("deviceType")}
                    >
                      {localizationStrings?.deviceType || "Device Type"}
                      <i className="fa fa-sort" aria-hidden="true" />
                    </th>
                    <th
                      className="inc-th-sort"
                      onClick={() => handleSortChange("model")}
                    >
                      {localizationStrings?.model || "Model"}
                      <i className="fa fa-sort" aria-hidden="true" />
                    </th>
                    <th
                      className="inc-th-sort"
                      onClick={() => handleSortChange("anomalyType")}
                    >
                      {localizationStrings?.anomalyType || "Anomaly Type"}
                      <i className="fa fa-sort" aria-hidden="true" />
                    </th>
                    <th
                      className="inc-th-sort"
                      onClick={() => handleSortChange("anomalyCount")}
                    >
                      {localizationStrings?.count || "Count"}
                      <i className="fa fa-sort" aria-hidden="true" />
                    </th>
                    <th
                      className="inc-th-sort"
                      onClick={() => handleSortChange("anomalySummary")}
                    >
                      {localizationStrings?.summary || "Summary"}
                      <i className="fa fa-sort" aria-hidden="true" />
                    </th>
                    <th
                      className="inc-th-sort"
                      onClick={() => handleSortChange("appliedSolution")}
                    >
                      {localizationStrings?.solution || "Solution"}
                      <i className="fa fa-sort" aria-hidden="true" />
                    </th>
                    <th
                      className="inc-th-sort"
                      onClick={() => handleSortChange("createdAt")}
                    >
                      {localizationStrings?.createdAt || "Created At"}
                      <i className="fa fa-sort" aria-hidden="true" />
                    </th>
                    <th>{localizationStrings?.action || "Action"}</th>
                  </tr>
                  <tr className="search-border">
                    {[
                      "serialNo",
                      "deviceType",
                      "model",
                      "anomalyType",
                      "anomalyCount",
                      "anomalySummary",
                      "appliedSolution",
                      "createdAt",
                    ].map((field) => (
                      <td key={field}>
                        <input
                          className="searchBox smallWidth"
                          type="text"
                          name={field}
                          onKeyUp={handleSearch}
                          placeholder={`Search ${field}`}
                        />
                        <i
                          className={`fa ${
                            sortOrder[field] === "asc"
                              ? "fa-sort-amount-desc"
                              : "fa-sort-amount-asc"
                          }`}
                          onClick={() => handleSortChange(field)}
                          aria-hidden="true"
                        ></i>
                      </td>
                    ))}
                    <td></td>
                  </tr>
                </thead>
                <tbody>
                  {currentAnomalies && currentAnomalies.length > 0 ? (
                    currentAnomalies.map((anomaly, index) => (
                      <tr key={index}>
                        <td>{anomaly.serialNo || "N/A"}</td>
                        <td>{anomaly.deviceType || "N/A"}</td>
                        <td>{anomaly.model || "N/A"}</td>
                        <td>{anomaly.anomalyType || "N/A"}</td>
                        <td>{anomaly.anomalyCount || 0}</td>
                        <td>{anomaly.anomalySummary || "N/A"}</td>
                        <td>{anomaly.appliedSolution || "N/A"}</td>
                        <td>
                          {new Date(anomaly.createdAt).toLocaleString() ||
                            "N/A"}
                        </td>
                        <td>
                          <button
                            className="text-btn primary-btn"
                            style={{
                              minWidth: "120px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title="Action"
                            disabled={
                              anomaly.appliedSolution &&
                              anomaly.appliedSolution.toLowerCase() !== "none"
                            }
                            onClick={() => handleSolution(anomaly.anomalyId)}
                          >
                            {{
                              per_client_anomaly: "Band Steering",
                              unbalanced_radio_traffic: "Band Steering",
                              high_multicast_ratio: "IGMP Snooping",
                              sudden_spike_drop: "Check Device/Network",
                            }[anomaly.anomalyType] || "Action"}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" style={{ textAlign: "center" }}>
                        {localizationStrings?.noData || "No Data"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="pagination-container">
          {totalFilteredAnomalies > 0 ? (
            <>
              <div className="pagination-info">
                <p>
                  {localizationStrings?.showing} {indexOfFirstAnomaly + 1}
                  {"-"}
                  {Math.min(
                    currentPage * anomaliesPerPage,
                    totalFilteredAnomalies
                  )}{" "}
                  {localizationStrings?.of} {totalFilteredAnomalies}{" "}
                  {localizationStrings?.records}
                </p>
              </div>
              <div className="pagination">
                <button onClick={handleFirstPage} disabled={currentPage === 1}>
                  {localizationStrings?.firstPage}
                </button>
                <button onClick={handlePrevPage} disabled={currentPage === 1}>
                  {localizationStrings?.previous}
                </button>
                {[...Array(Math.min(totalPages, 10)).keys()].map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageClick(page + 1)}
                    className={currentPage === page + 1 ? "active" : ""}
                  >
                    {page + 1}
                  </button>
                ))}
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  {localizationStrings?.next}
                </button>
                <button
                  onClick={handleLastPage}
                  disabled={currentPage === totalPages}
                >
                  {localizationStrings?.lastPage}
                </button>
              </div>
            </>
          ) : (
            <div>
              <p>{localizationStrings?.noRecords}</p>
            </div>
          )}
        </div>
      </div>
      {showFailurePopup && (
        <div
          className="customModal confirm-modal"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="modal-dialog modal-dialog-centered small">
            <div
              className="modal-content inc-modal"
              style={{ padding: "32px 24px", textAlign: "center" }}
            >
              <div className="modal-body">
                <img
                  src={errorIcon}
                  alt="error"
                  style={{ width: "48px", marginBottom: "16px" }}
                />
                <h3 style={{ color: "red", marginBottom: "16px" }}>
                  {errorMessage ||
                    localizationStrings?.anomalyFailure ||
                    "Failed to apply the solution"}
                </h3>
                <div
                  className="modal-footer"
                  style={{ justifyContent: "center" }}
                >
                  <button
                    className="text-btn primary-btn"
                    style={{ minWidth: "100px", padding: "8px 24px" }}
                    onClick={() => setShowFailurePopup(false)}
                  >
                    {localizationStrings?.close || "Close"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        show={showSuccessPopup}
        onHide={handleCancelPopUp}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="inc-alert index-2000"
      >
        <span className="success-icon">
          <i className="icon done"></i>
        </span>
        <h3>{localizationStrings?.success}</h3>
        <p>{localizationStrings?.anomalySuccess}</p>
        <Button onClick={handleCancelPopUp}>{localizationStrings?.ok}</Button>
      </Modal>
    </div>
  );
}

export default Anomalies;
